import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2, ArrowLeft, LogOut, Plus, Trash2, Loader2, RefreshCw, KeyRound,
  Upload, FileText, TrendingUp, TrendingDown, Receipt, X, Edit3, Shield,
  Calculator, PieChart as PieChartIcon, ClipboardList, CheckCircle2, AlertCircle,
  ChevronRight, Layers, Download, ExternalLink, Copy, Eye,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import ModuleKanbanMenu from '../components/ModuleKanbanMenu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const parseError = (err, fallback = 'Error') => {
  const d = err?.response?.data?.detail;
  if (Array.isArray(d)) return d.map(x => x?.msg || x).join(' | ');
  return d || err?.message || fallback;
};
const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('es-EC') : '—';

const TIPO_LABEL = {
  factura: 'Factura', nota_credito: 'Nota Crédito', nota_debito: 'Nota Débito',
  retencion: 'Retención', liquidacion: 'Liquidación', otro: 'Otro',
};
const TIPO_BADGE = {
  factura: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  nota_credito: 'bg-amber-50 text-amber-700 border-amber-200',
  nota_debito: 'bg-orange-50 text-orange-700 border-orange-200',
  retencion: 'bg-purple-50 text-purple-700 border-purple-200',
  liquidacion: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  otro: 'bg-gray-50 text-gray-700 border-gray-200',
};
const GP_LABEL = {
  vivienda: 'Vivienda', educacion: 'Educación', salud: 'Salud',
  alimentacion: 'Alimentación', vestimenta: 'Vestimenta', turismo: 'Turismo',
};

const ContabilidadPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWs, setActiveWs] = useState(null);
  const [tab, setTab] = useState('resumen');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showCredsFor, setShowCredsFor] = useState(null);
  const [showSyncFor, setShowSyncFor] = useState(null);
  const [showImportClavesFor, setShowImportClavesFor] = useState(null);
  const [showUploadFor, setShowUploadFor] = useState(null);
  const [docs, setDocs] = useState([]);
  const [summary, setSummary] = useState(null);
  const now = new Date();
  const [period, setPeriod] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [filters, setFilters] = useState({ direccion: 'recibidos', tipo: '', categoria: '', gasto_personal: '' });
  const [categorias, setCategorias] = useState({ categorias_gasto: [], gastos_personales: [] });
  const [syncJob, setSyncJob] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (!stored) { navigate('/login'); return; }
    const u = JSON.parse(stored);
    const allowed = ['admin', 'gerencia', 'finanzas', 'contratos', 'contabilidad'].includes(u.role);
    if (!allowed) { toast.error('Acceso restringido'); navigate('/login'); return; }
    setUser(u);
  }, [navigate]);

  const loadWs = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/contabilidad/workspaces`, { headers: authHeaders() });
      setWorkspaces(r.data);
      setActiveWs(prev => {
        if (!prev) return r.data[0] || null;
        // Refresh active with fresh data (has_credentials, last_sync, etc.)
        const fresh = r.data.find(w => w.id === prev.id);
        return fresh || r.data[0] || null;
      });
    } catch (e) { toast.error(parseError(e)); }
  }, []);

  const loadCategorias = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/contabilidad/categorias`, { headers: authHeaders() });
      setCategorias(r.data);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!user) return;
    (async () => { await loadWs(); await loadCategorias(); setLoading(false); })();
  }, [user, loadWs, loadCategorias]);

  const loadDocs = useCallback(async () => {
    if (!activeWs) return;
    try {
      const params = new URLSearchParams();
      params.set('year', period.year);
      params.set('month', period.month);
      if (filters.direccion) params.set('direccion', filters.direccion);
      if (filters.tipo) params.set('tipo', filters.tipo);
      if (filters.categoria) params.set('categoria', filters.categoria);
      if (filters.gasto_personal) params.set('gasto_personal', filters.gasto_personal);
      const r = await axios.get(`${API}/contabilidad/workspaces/${activeWs.id}/documents?${params}`, { headers: authHeaders() });
      setDocs(r.data);
    } catch (e) { toast.error(parseError(e)); }
  }, [activeWs, period, filters]);

  const loadSummary = useCallback(async () => {
    if (!activeWs) return;
    try {
      const r = await axios.get(`${API}/contabilidad/workspaces/${activeWs.id}/summary?year=${period.year}&month=${period.month}`, { headers: authHeaders() });
      setSummary(r.data);
    } catch (e) { toast.error(parseError(e)); }
  }, [activeWs, period]);

  useEffect(() => { loadDocs(); loadSummary(); }, [loadDocs, loadSummary]);

  const pollSyncJob = useCallback(async (jobId, wsId) => {
    if (!jobId) return;
    try {
      const r = await axios.get(`${API}/contabilidad/workspaces/${wsId}/sync/${jobId}`, { headers: authHeaders() });
      setSyncJob(r.data);
      if (r.data.status === 'running') {
        setTimeout(() => pollSyncJob(jobId, wsId), 2500);
      } else {
        toast[r.data.status === 'success' ? 'success' : 'error'](r.data.message);
        loadWs(); loadDocs(); loadSummary();
      }
    } catch (e) { /* keep polling on transient */ }
  }, [loadWs, loadDocs, loadSummary]);

  const startSync = async (year, month, direccion, tipos) => {
    if (!activeWs) return;
    try {
      const r = await axios.post(`${API}/contabilidad/workspaces/${activeWs.id}/sync`,
        { year: +year, month: +month, direccion, tipos },
        { headers: authHeaders() });
      toast.info('Sincronización iniciada. Esto puede tomar 1-3 minutos.');
      setShowSyncFor(null);
      setSyncJob({ id: r.data.job_id, status: 'running', progress: 0, message: 'Iniciando…' });
      pollSyncJob(r.data.job_id, activeWs.id);
    } catch (e) { toast.error(parseError(e)); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0F1225] text-white"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#F5F6FA]" data-testid="contabilidad-page">
      {/* HEADER */}
      <div className="bg-[#0F1225] text-white shadow">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <div className="flex items-center gap-2 text-lg font-bold"><Calculator className="w-5 h-5 text-emerald-400" /> Contabilidad SRI</div>
              <div className="text-xs text-white/60">Ecuador · Ingresos · Egresos · Retenciones · IVA · Gastos Personales</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-white/70">Bienvenido, <strong>{user?.username}</strong></span>
            <Button variant="outline" size="sm" onClick={() => { localStorage.clear(); navigate('/login'); }}
                    className="text-white border-white/30 hover:bg-white/10">
              <LogOut className="w-4 h-4 mr-1" /> Salir
            </Button>
          </div>
        </div>
      </div>

      {/* Kanban Module Menu */}
      <ModuleKanbanMenu user={user} active="contabilidad" />

      <div className="max-w-[1600px] mx-auto px-6 py-6 space-y-4">
        {/* WORKSPACE SELECTOR */}
        <div className="bg-white rounded-xl border p-4 flex flex-wrap items-center gap-3" data-testid="workspace-bar">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[#2F3D8E]" />
            <span className="text-sm font-semibold text-gray-700">Empresa:</span>
          </div>
          {workspaces.length === 0 ? (
            <span className="text-sm text-gray-400">No hay empresas registradas.</span>
          ) : (
            <select value={activeWs?.id || ''}
                    onChange={e => setActiveWs(workspaces.find(w => w.id === e.target.value))}
                    className="px-3 py-1.5 border rounded-lg text-sm min-w-[280px]" data-testid="workspace-selector">
              {workspaces.map(w => (
                <option key={w.id} value={w.id}>{w.razon_social} · RUC {w.ruc}</option>
              ))}
            </select>
          )}
          <div className="ml-auto flex flex-wrap gap-2">
            <Button size="sm" onClick={() => setShowCreate(true)} data-testid="add-workspace-btn">
              <Plus className="w-4 h-4 mr-1" /> Nueva empresa
            </Button>
            {activeWs && (
              <>
                <Button size="sm" variant="outline" onClick={() => setShowCredsFor(activeWs)} data-testid="creds-btn">
                  <KeyRound className="w-4 h-4 mr-1" /> Credenciales SRI
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowSyncFor(activeWs)}
                        disabled={!activeWs.has_credentials} data-testid="sync-btn">
                  <RefreshCw className="w-4 h-4 mr-1" /> Sincronizar SRI
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowImportClavesFor(activeWs)}
                        className="border-emerald-500 text-emerald-700 hover:bg-emerald-50" data-testid="import-claves-btn">
                  <Download className="w-4 h-4 mr-1" /> Modo Asistido
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowUploadFor(activeWs)} data-testid="upload-btn">
                  <Upload className="w-4 h-4 mr-1" /> Subir XML
                </Button>
                <Button size="sm" variant="ghost" onClick={() => {
                  if (window.confirm(`¿Eliminar workspace ${activeWs.razon_social}? Se perderán todos los documentos asociados.`)) {
                    axios.delete(`${API}/contabilidad/workspaces/${activeWs.id}`, { headers: authHeaders() })
                      .then(() => { toast.success('Eliminado'); setActiveWs(null); loadWs(); })
                      .catch(e => toast.error(parseError(e)));
                  }
                }} className="text-red-600 hover:bg-red-50" data-testid="delete-ws-btn">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </div>

        {activeWs && (
          <>
            {/* WS Info Card + Last sync */}
            <div className="bg-white rounded-xl border p-4 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <div className="text-xs text-gray-500">Razón Social</div>
                <div className="font-semibold text-sm">{activeWs.razon_social}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">RUC / Tipo</div>
                <div className="font-mono text-sm">{activeWs.ruc} · {activeWs.tipo_contribuyente === 'juridica' ? 'Jurídica' : 'Natural'}</div>
                <div className="text-[10px] text-gray-400">{activeWs.obligado_contabilidad ? 'Obligado a llevar contabilidad' : 'No obligado'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Credenciales SRI</div>
                <div className="text-sm flex items-center gap-1">
                  {activeWs.has_credentials
                    ? <span className="inline-flex items-center gap-1 text-emerald-700"><Shield className="w-3.5 h-3.5" /> Configuradas</span>
                    : <span className="inline-flex items-center gap-1 text-amber-700"><AlertCircle className="w-3.5 h-3.5" /> Sin configurar</span>}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Última sincronización</div>
                <div className="text-sm">{activeWs.last_sync_at ? new Date(activeWs.last_sync_at).toLocaleString('es-EC') : '—'}</div>
                <div className="text-[10px] text-gray-400 truncate max-w-[240px]" title={activeWs.last_sync_message}>{activeWs.last_sync_message || ''}</div>
              </div>
            </div>

            {/* PERIOD SELECTOR */}
            <div className="bg-white rounded-xl border p-3 flex items-center gap-3 flex-wrap" data-testid="period-bar">
              <span className="text-sm font-semibold text-gray-700">Período:</span>
              <select value={period.year} onChange={e => setPeriod(p => ({ ...p, year: +e.target.value }))}
                      className="px-2 py-1 border rounded text-sm" data-testid="year-select">
                {[0, 1, 2, 3, 4].map(off => {
                  const y = now.getFullYear() - off;
                  return <option key={y} value={y}>{y}</option>;
                })}
              </select>
              <select value={period.month} onChange={e => setPeriod(p => ({ ...p, month: +e.target.value }))}
                      className="px-2 py-1 border rounded text-sm" data-testid="month-select">
                {['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'].map((m, i) => (
                  <option key={i} value={i + 1}>{m}</option>
                ))}
              </select>
              <div className="ml-auto flex gap-1 bg-gray-100 rounded-lg p-1">
                {['resumen', 'ingresos', 'egresos', 'retenciones', 'gastos_personales', 'declaraciones'].map(t => (
                  <button key={t} onClick={() => setTab(t)}
                          className={`px-3 py-1.5 rounded-md text-sm capitalize transition ${tab === t ? 'bg-white shadow font-semibold text-[#2F3D8E]' : 'text-gray-500 hover:text-gray-800'}`}
                          data-testid={`tab-${t}`}>
                    {t.replace('_', ' ')}
                  </button>
                ))}
              </div>
            </div>

            {/* SYNC PROGRESS */}
            {syncJob && syncJob.status === 'running' && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-center gap-2 text-blue-700 mb-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm font-semibold">Sincronizando con SRI…</span>
                  <span className="text-xs">{syncJob.progress}%</span>
                </div>
                <div className="bg-white rounded-full h-2 overflow-hidden">
                  <div className="bg-blue-500 h-full transition-all" style={{ width: `${syncJob.progress}%` }}></div>
                </div>
                <div className="text-xs text-blue-600 mt-1">{syncJob.message}</div>
              </div>
            )}

            {/* TABS CONTENT */}
            {tab === 'resumen' && <ResumenTab summary={summary} period={period} />}
            {tab === 'ingresos' && (
              <DocumentTable docs={docs} type="ingresos" filters={filters} setFilters={setFilters}
                             onReload={() => { loadDocs(); loadSummary(); }} categorias={categorias} />
            )}
            {tab === 'egresos' && (
              <DocumentTable docs={docs} type="egresos" filters={filters} setFilters={setFilters}
                             onReload={() => { loadDocs(); loadSummary(); }} categorias={categorias} />
            )}
            {tab === 'retenciones' && (
              <RetencionesTab docs={docs.filter(d => d.tipo_comprobante === 'retencion')} />
            )}
            {tab === 'gastos_personales' && (
              <GastosPersonalesTab summary={summary} docs={docs.filter(d => d.direccion === 'recibidos')}
                                    onReload={loadDocs} categorias={categorias} />
            )}
            {tab === 'declaraciones' && (
              <DeclaracionesTab summary={summary} period={period} ws={activeWs} />
            )}
          </>
        )}
      </div>

      {showCreate && <CreateWorkspaceModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); loadWs(); }} />}
      {showCredsFor && <CredentialsModal ws={showCredsFor} onClose={() => setShowCredsFor(null)} onSaved={() => { setShowCredsFor(null); loadWs(); }} />}
      {showSyncFor && <SyncModal ws={showSyncFor} onClose={() => setShowSyncFor(null)} onStart={startSync} />}
      {showImportClavesFor && <ImportClavesModal ws={showImportClavesFor} onClose={() => setShowImportClavesFor(null)} onDone={() => { setShowImportClavesFor(null); loadDocs(); loadSummary(); loadWs(); }} />}
      {showUploadFor && <UploadXmlModal ws={showUploadFor} onClose={() => setShowUploadFor(null)} onDone={() => { setShowUploadFor(null); loadDocs(); loadSummary(); }} />}
    </div>
  );
};

// ═════════ MODALS ═════════

const CreateWorkspaceModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ ruc: '', razon_social: '', tipo_contribuyente: 'juridica', obligado_contabilidad: true });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.ruc || !form.razon_social) { toast.error('Completa RUC y Razón Social'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/contabilidad/workspaces`, form, { headers: authHeaders() });
      toast.success('Empresa creada'); onCreated();
    } catch (e) { toast.error(parseError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-[#0F1225]">Nueva Empresa Contable</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">RUC (13 dígitos) o Cédula (10)</Label>
            <Input value={form.ruc} onChange={e => setForm({ ...form, ruc: e.target.value.replace(/\D/g, '').slice(0, 13) })} data-testid="ws-ruc" />
          </div>
          <div>
            <Label className="text-xs">Razón Social</Label>
            <Input value={form.razon_social} onChange={e => setForm({ ...form, razon_social: e.target.value })} data-testid="ws-razon" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Tipo</Label>
              <select value={form.tipo_contribuyente} onChange={e => setForm({ ...form, tipo_contribuyente: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md text-sm">
                <option value="natural">Persona Natural</option>
                <option value="juridica">Persona Jurídica</option>
              </select>
            </div>
            <div className="flex items-end pb-1">
              <label className="text-xs flex items-center gap-2">
                <input type="checkbox" checked={form.obligado_contabilidad}
                        onChange={e => setForm({ ...form, obligado_contabilidad: e.target.checked })} />
                Obligado a llevar contabilidad
              </label>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" size="sm" onClick={onClose}>Cancelar</Button>
          <Button size="sm" onClick={submit} disabled={saving} data-testid="ws-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crear'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const CredentialsModal = ({ ws, onClose, onSaved }) => {
  const [form, setForm] = useState({ sri_user: '', sri_password: '' });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.sri_user || !form.sri_password) { toast.error('Ambos campos requeridos'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/contabilidad/workspaces/${ws.id}/sri-credentials`, form, { headers: authHeaders() });
      toast.success('Credenciales guardadas de forma segura (AES)'); onSaved();
    } catch (e) { toast.error(parseError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-[#0F1225] flex items-center gap-2"><KeyRound className="w-5 h-5 text-emerald-600" /> Credenciales SRI</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-gray-500 mb-3">Se usan para hacer scraping del portal <code>srienlinea.sri.gob.ec</code>. Se almacenan encriptadas (AES-Fernet).</p>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Cédula / RUC</Label>
            <Input value={form.sri_user} onChange={e => setForm({ ...form, sri_user: e.target.value })} data-testid="sri-user" />
          </div>
          <div>
            <Label className="text-xs">Clave SRI</Label>
            <Input type="password" value={form.sri_password} onChange={e => setForm({ ...form, sri_password: e.target.value })} data-testid="sri-password" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" size="sm" onClick={onClose}>Cancelar</Button>
          <Button size="sm" onClick={submit} disabled={saving} data-testid="creds-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const SyncModal = ({ ws, onClose, onStart }) => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [direccion, setDireccion] = useState('recibidos');
  const [tipos, setTipos] = useState(['factura', 'nota_credito', 'retencion']);
  const toggleTipo = t => setTipos(tt => tt.includes(t) ? tt.filter(x => x !== t) : [...tt, t]);
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-[#0F1225] flex items-center gap-2"><RefreshCw className="w-5 h-5 text-blue-600" /> Sincronizar SRI</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-gray-500 mb-3">Se abrirá una sesión en el portal SRI usando tus credenciales y se descargarán los XML del mes seleccionado. Puede tomar 1-3 min.</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Año</Label>
            <select value={year} onChange={e => setYear(+e.target.value)} className="w-full px-3 py-2 border rounded-md text-sm" data-testid="sync-year">
              {[0, 1, 2, 3, 4].map(o => { const y = now.getFullYear() - o; return <option key={y} value={y}>{y}</option>; })}
            </select>
          </div>
          <div>
            <Label className="text-xs">Mes</Label>
            <select value={month} onChange={e => setMonth(+e.target.value)} className="w-full px-3 py-2 border rounded-md text-sm" data-testid="sync-month">
              {['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'].map((m, i) => (
                <option key={i} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Dirección</Label>
            <div className="flex gap-2 mt-1">
              {['recibidos', 'emitidos'].map(d => (
                <button key={d} onClick={() => setDireccion(d)}
                        className={`flex-1 px-3 py-2 rounded-md text-sm border transition ${direccion === d ? 'bg-[#2F3D8E] text-white border-[#2F3D8E]' : 'bg-white text-gray-700'}`}
                        data-testid={`sync-dir-${d}`}>
                  {d === 'recibidos' ? 'Recibidos (Egresos)' : 'Emitidos (Ingresos)'}
                </button>
              ))}
            </div>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Tipos de comprobante</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {[['factura', 'Factura'], ['nota_credito', 'N. Crédito'], ['nota_debito', 'N. Débito'], ['retencion', 'Retención'], ['liquidacion', 'Liquidación']].map(([v, l]) => (
                <label key={v} className="text-xs flex items-center gap-1 px-2 py-1 border rounded-md">
                  <input type="checkbox" checked={tipos.includes(v)} onChange={() => toggleTipo(v)} />
                  {l}
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" size="sm" onClick={onClose}>Cancelar</Button>
          <Button size="sm" onClick={() => onStart(year, month, direccion, tipos)} data-testid="sync-start">
            <RefreshCw className="w-4 h-4 mr-1" /> Iniciar
          </Button>
        </div>
      </div>
    </div>
  );
};

const UploadXmlModal = ({ ws, onClose, onDone }) => {
  const [files, setFiles] = useState([]);
  const [direccion, setDireccion] = useState('recibidos');
  const [uploading, setUploading] = useState(false);
  const submit = async () => {
    if (files.length === 0) { toast.error('Selecciona al menos un archivo'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const r = await axios.post(`${API}/contabilidad/workspaces/${ws.id}/upload-xml?direccion=${direccion}`,
        fd, { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } });
      toast.success(`${r.data.saved} guardados, ${r.data.duplicated} duplicados, ${r.data.errored.length} errores`);
      onDone();
    } catch (e) { toast.error(parseError(e)); } finally { setUploading(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-[#0F1225] flex items-center gap-2"><Upload className="w-5 h-5 text-orange-600" /> Subir XMLs manualmente</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Dirección</Label>
            <div className="flex gap-2 mt-1">
              {['recibidos', 'emitidos'].map(d => (
                <button key={d} onClick={() => setDireccion(d)}
                        className={`flex-1 px-3 py-2 rounded-md text-sm border transition ${direccion === d ? 'bg-[#2F3D8E] text-white border-[#2F3D8E]' : 'bg-white text-gray-700'}`}>
                  {d === 'recibidos' ? 'Recibidos' : 'Emitidos'}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Archivos XML (múltiples)</Label>
            <input type="file" accept=".xml,application/xml,text/xml" multiple
                   onChange={e => setFiles(Array.from(e.target.files))}
                   className="mt-1 block w-full text-sm" data-testid="upload-files" />
            <div className="text-xs text-gray-500 mt-1">{files.length} archivo(s) seleccionado(s)</div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" size="sm" onClick={onClose}>Cancelar</Button>
          <Button size="sm" onClick={submit} disabled={uploading} data-testid="upload-submit">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Subir y parsear'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const ImportClavesModal = ({ ws, onClose, onDone }) => {
  const [step, setStep] = useState(1); // 1=instrucciones, 2=input, 3=proceso
  const [direccion, setDireccion] = useState('recibidos');
  const [rawText, setRawText] = useState('');
  const [file, setFile] = useState(null);
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);

  const detectedClaves = useMemo(() => {
    const matches = (rawText || '').match(/\b\d{49}\b/g) || [];
    return Array.from(new Set(matches));
  }, [rawText]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Copiado al portapapeles'));
  };

  const start = async () => {
    setStarting(true);
    try {
      let r;
      if (file) {
        const fd = new FormData();
        fd.append('file', file);
        r = await axios.post(`${API}/contabilidad/workspaces/${ws.id}/import-claves-file?direccion=${direccion}`,
          fd, { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } });
      } else {
        r = await axios.post(`${API}/contabilidad/workspaces/${ws.id}/import-claves`,
          { raw_text: rawText, direccion }, { headers: authHeaders() });
      }
      setJob({ id: r.data.job_id, total: r.data.total_claves, progress: 0, message: 'Iniciando…', status: 'running' });
      setStep(3);
    } catch (e) { toast.error(parseError(e)); } finally { setStarting(false); }
  };

  useEffect(() => {
    if (!job || job.status !== 'running') return;
    const iv = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/contabilidad/workspaces/${ws.id}/sync/${job.id}`, { headers: authHeaders() });
        setJob(prev => ({ ...prev, ...r.data }));
        if (r.data.status === 'success' || r.data.status === 'error') {
          clearInterval(iv);
          if (r.data.status === 'success') {
            toast.success(`Import completado: ${r.data.count || 0} guardados`);
          } else {
            toast.error(`Error: ${r.data.message}`);
          }
        }
      } catch (e) { /* silent */ }
    }, 2500);
    return () => clearInterval(iv);
  }, [job?.id, job?.status, ws.id]);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-lg font-bold text-[#0F1225] flex items-center gap-2">
              <Download className="w-5 h-5 text-emerald-600" /> Modo Asistido — Importar por Claves de Acceso
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">Bypasea el reCAPTCHA del SRI descargando el reporte manualmente. Los XML se bajan desde el API público del SRI.</p>
          </div>
          <button onClick={onClose} data-testid="import-claves-close"><X className="w-5 h-5" /></button>
        </div>

        {/* Steps indicator */}
        <div className="flex items-center justify-center gap-2 py-3 bg-gray-50 border-b">
          {[1, 2, 3].map(s => (
            <div key={s} className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step >= s ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
              {s}
            </div>
          ))}
        </div>

        <div className="p-6">
          {step === 1 && (
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800">1️⃣ Descarga el reporte desde el portal SRI</h4>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-2 text-sm text-gray-700">
                <p><strong>Por qué este modo:</strong> el SRI implementó reCAPTCHA v3 (invisible) en la búsqueda de comprobantes recibidos. Los navegadores automatizados obtienen un score bajo y el SRI rechaza silenciosamente la consulta. En cambio, cuando <strong>tú resuelves el reCAPTCHA manualmente</strong> (basta con estar logueado y clickear Consultar como humano), el listado aparece y podés descargar el reporte.</p>
              </div>
              <ol className="list-decimal ml-5 space-y-2 text-sm text-gray-800">
                <li>
                  Abre el portal SRI en <strong>una pestaña nueva</strong>:{' '}
                  <a href="https://srienlinea.sri.gob.ec/tuportal-internet/accederAplicacion.jspa?redireccion=57&idGrupo=55"
                     target="_blank" rel="noreferrer"
                     className="text-emerald-700 underline inline-flex items-center gap-1">
                    Comprobantes Electrónicos Recibidos <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
                <li>Inicia sesión con tu RUC/Cédula y clave.</li>
                <li>Selecciona el <strong>Año</strong>, <strong>Mes</strong>, <strong>Día</strong> (elige &quot;Todos&quot; para el mes completo) y <strong>Tipo de comprobante</strong> (Factura, Notas de Crédito, etc.).</li>
                <li>Clickea <strong>&quot;Consultar&quot;</strong> — el reCAPTCHA se resuelve automáticamente porque sos humano navegando.</li>
                <li>Cuando aparezca el listado con las filas, clickea el ícono <strong>&quot;Descargar reporte&quot;</strong> (ícono azul con flecha hacia abajo). Se descargará un archivo <code>.txt</code> con la lista de comprobantes del mes.</li>
                <li>Regresa acá, y en el siguiente paso <strong>arrastra ese TXT</strong> o pega su contenido.</li>
              </ol>
              <div className="text-xs text-gray-500 bg-yellow-50 border border-yellow-200 rounded p-2">
                💡 <strong>Alternativa</strong>: podés repetir el proceso una vez por tipo (Factura, Retención, etc.) o combinar varios reportes TXT en un mismo import.
              </div>
              <div className="flex justify-end">
                <Button onClick={() => setStep(2)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="import-claves-next-1">
                  Ya descargué el reporte →
                </Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800">2️⃣ Sube el TXT o pega las claves de acceso</h4>
              <div>
                <Label className="text-xs">Dirección</Label>
                <div className="flex gap-2 mt-1">
                  {['recibidos', 'emitidos'].map(d => (
                    <button key={d} onClick={() => setDireccion(d)}
                            className={`flex-1 px-3 py-2 rounded-md text-sm border transition ${direccion === d ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-700'}`}>
                      {d === 'recibidos' ? 'Recibidos' : 'Emitidos'}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-xs">Opción A: Sube el archivo .txt descargado del SRI</Label>
                <input type="file" accept=".txt,.csv,text/plain,text/csv"
                       onChange={e => { setFile(e.target.files[0]); setRawText(''); }}
                       className="mt-1 block w-full text-sm" data-testid="import-claves-file" />
                {file && <div className="text-xs text-emerald-700 mt-1">📄 {file.name} ({(file.size / 1024).toFixed(1)} KB)</div>}
              </div>

              <div className="text-center text-xs text-gray-400">— o —</div>

              <div>
                <Label className="text-xs">Opción B: Pega el contenido del reporte o solo las claves (49 dígitos por línea)</Label>
                <textarea value={rawText}
                          onChange={e => { setRawText(e.target.value); setFile(null); }}
                          rows={6}
                          placeholder="Pega aquí el contenido del TXT del SRI, o una clave de acceso por línea. Sistema detecta automáticamente los 49 dígitos."
                          className="mt-1 block w-full text-xs font-mono border rounded-md p-2"
                          data-testid="import-claves-textarea" />
                {detectedClaves.length > 0 && (
                  <div className="text-xs text-emerald-700 mt-1">✓ {detectedClaves.length} clave(s) de acceso detectada(s)</div>
                )}
              </div>

              <div className="flex justify-between">
                <Button variant="outline" size="sm" onClick={() => setStep(1)}>← Volver</Button>
                <Button onClick={start} disabled={starting || (!file && detectedClaves.length === 0)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="import-claves-start">
                  {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Descargar XMLs desde SRI →'}
                </Button>
              </div>
            </div>
          )}

          {step === 3 && job && (
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800">3️⃣ Descargando XMLs del SRI</h4>
              <div className="bg-gray-50 border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Job:</span>
                  <code className="text-xs text-gray-800">{job.id}</code>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div className="h-full bg-emerald-600 transition-all" style={{ width: `${job.progress || 0}%` }}></div>
                </div>
                <div className="text-sm text-gray-700">{job.message}</div>
                {job.status === 'success' && (
                  <div className="flex items-center gap-2 text-emerald-700 font-medium">
                    <CheckCircle2 className="w-5 h-5" /> Completado: {job.count} XMLs guardados
                  </div>
                )}
                {job.status === 'error' && (
                  <div className="flex items-center gap-2 text-red-700 font-medium">
                    <AlertCircle className="w-5 h-5" /> Error: {job.message}
                  </div>
                )}
                {job.errors && job.errors.length > 0 && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-red-600">{job.errors.length} errores individuales (click para ver)</summary>
                    <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                      {job.errors.slice(0, 20).map((e, i) => (
                        <li key={i} className="text-gray-600">
                          <code className="text-[10px]">{e.clave_acceso?.slice(0, 20)}…</code> — {e.error}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
              <div className="flex justify-end">
                <Button onClick={job.status !== 'running' ? onDone : onClose}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  {job.status === 'running' ? 'Ejecutando en segundo plano — Cerrar' : 'Ver documentos importados'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ═════════ DOCUMENT DETAIL MODAL ═════════

const DocumentDetailModal = ({ docId, onClose }) => {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/contabilidad/documents/${docId}`, { headers: authHeaders() })
      .then(r => { if (alive) setDoc(r.data); })
      .catch(e => toast.error(parseError(e)))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [docId]);

  const downloadXml = () => {
    const token = localStorage.getItem('token');
    const url = `${API}/contabilidad/documents/${docId}/xml`;
    // fetch as blob to include auth header
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${doc?.clave_acceso || 'comprobante'}.xml`;
        a.click();
      })
      .catch(e => toast.error('No se pudo descargar el XML'));
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-5xl w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-lg font-bold text-[#0F1225] flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" /> Detalle del Comprobante
            </h3>
            {doc && (
              <p className="text-xs text-gray-500 mt-0.5 font-mono">
                {TIPO_LABEL[doc.tipo_comprobante] || doc.tipo_comprobante} · {doc.numero_documento} · {fmtDate(doc.fecha_emision)}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {doc && (
              <Button size="sm" variant="outline" onClick={downloadXml} data-testid="doc-download-xml">
                <Download className="w-4 h-4 mr-1" /> XML
              </Button>
            )}
            <button onClick={onClose} data-testid="doc-detail-close"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {loading && (
          <div className="p-10 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500" /></div>
        )}

        {doc && !loading && (
          <div className="p-6 space-y-6">
            {/* Encabezado tributario */}
            <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-1">
                <div className="text-xs uppercase text-blue-700 font-semibold">Emisor</div>
                <div className="font-semibold text-gray-900">{doc.razon_social_emisor}</div>
                <div className="text-sm text-gray-600 font-mono">RUC: {doc.ruc_emisor}</div>
                <div className="text-xs text-gray-500">Estab: {doc.estab} · Pto Emi: {doc.pto_emi} · Sec: {doc.secuencial}</div>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-1">
                <div className="text-xs uppercase text-emerald-700 font-semibold">Receptor</div>
                <div className="font-semibold text-gray-900">{doc.razon_social_receptor || '—'}</div>
                <div className="text-sm text-gray-600 font-mono">
                  {doc.tipo_identificacion_receptor && `${doc.tipo_identificacion_receptor}: `}
                  {doc.ruc_receptor || '—'}
                </div>
                {doc.periodo_fiscal && <div className="text-xs text-gray-500">Período fiscal: {doc.periodo_fiscal}</div>}
              </div>
            </section>

            {/* Autorización */}
            <section className="bg-gray-50 border rounded-lg p-4 grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
              <div><div className="text-[10px] text-gray-500 uppercase">Estado</div><div className="font-semibold text-emerald-700">{doc.estado || '—'}</div></div>
              <div><div className="text-[10px] text-gray-500 uppercase">Ambiente</div><div>{doc.ambiente || '—'}</div></div>
              <div className="col-span-2"><div className="text-[10px] text-gray-500 uppercase">Clave de Acceso</div><div className="font-mono text-[11px] break-all">{doc.clave_acceso}</div></div>
              <div className="col-span-4"><div className="text-[10px] text-gray-500 uppercase">Número Autorización / Fecha</div><div className="font-mono text-[11px] break-all">{doc.numero_autorizacion || '—'} · {doc.fecha_autorizacion || '—'}</div></div>
            </section>

            {/* Detalles / items */}
            {doc.detalles && doc.detalles.length > 0 && (
              <section>
                <h4 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
                  <ClipboardList className="w-4 h-4" /> Detalle de líneas ({doc.detalles.length})
                </h4>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-100 text-gray-700">
                      <tr>
                        <th className="px-3 py-2 text-left">Cód. Ppal.</th>
                        <th className="px-3 py-2 text-left">Cód. Aux.</th>
                        <th className="px-3 py-2 text-left">Descripción</th>
                        <th className="px-3 py-2 text-right">Cant.</th>
                        <th className="px-3 py-2 text-right">P. Unit.</th>
                        <th className="px-3 py-2 text-right">Desc.</th>
                        <th className="px-3 py-2 text-right">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {doc.detalles.map((it, i) => (
                        <tr key={i} className="border-t hover:bg-gray-50">
                          <td className="px-3 py-2 font-mono text-[10px]">{it.codigo_principal || '—'}</td>
                          <td className="px-3 py-2 font-mono text-[10px]">{it.codigo_auxiliar || '—'}</td>
                          <td className="px-3 py-2">{it.descripcion || '—'}</td>
                          <td className="px-3 py-2 text-right">{Number(it.cantidad || 0).toLocaleString('en-US', { maximumFractionDigits: 4 })}</td>
                          <td className="px-3 py-2 text-right">{fmtMoney(it.precio_unitario)}</td>
                          <td className="px-3 py-2 text-right text-red-600">{fmtMoney(it.descuento)}</td>
                          <td className="px-3 py-2 text-right font-semibold">{fmtMoney(it.precio_total_sin_impuesto)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* Retenciones (para comprobantes de retención) */}
            {doc.retenciones && doc.retenciones.length > 0 && (
              <section>
                <h4 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
                  <Shield className="w-4 h-4" /> Retenciones ({doc.retenciones.length})
                </h4>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-100 text-gray-700">
                      <tr>
                        <th className="px-3 py-2 text-left">Código</th>
                        <th className="px-3 py-2 text-left">Cód. Retención</th>
                        <th className="px-3 py-2 text-right">Base Imp.</th>
                        <th className="px-3 py-2 text-right">Porcentaje</th>
                        <th className="px-3 py-2 text-right">Retenido</th>
                      </tr>
                    </thead>
                    <tbody>
                      {doc.retenciones.map((r, i) => (
                        <tr key={i} className="border-t hover:bg-gray-50">
                          <td className="px-3 py-2 font-mono">{r.codigo}</td>
                          <td className="px-3 py-2 font-mono">{r.codigo_retencion}</td>
                          <td className="px-3 py-2 text-right">{fmtMoney(r.base_imponible)}</td>
                          <td className="px-3 py-2 text-right">{Number(r.porcentaje || 0).toFixed(2)}%</td>
                          <td className="px-3 py-2 text-right font-semibold text-red-700">{fmtMoney(r.valor_retenido)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* Totales & impuestos */}
            <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white border rounded-lg p-4">
                <h4 className="text-sm font-bold text-gray-800 mb-3">Impuestos por porcentaje</h4>
                <table className="w-full text-xs">
                  <tbody className="[&>tr>td]:py-1">
                    <tr className="border-b"><td className="text-gray-600">Subtotal 0%</td><td className="text-right font-medium">{fmtMoney(doc.subtotal_0)}</td></tr>
                    <tr className="border-b"><td className="text-gray-600">Subtotal 12%</td><td className="text-right font-medium">{fmtMoney(doc.subtotal_12)}</td></tr>
                    <tr className="border-b"><td className="text-gray-600">Subtotal 15%</td><td className="text-right font-medium">{fmtMoney(doc.subtotal_15)}</td></tr>
                    <tr className="border-b"><td className="text-gray-600">No Objeto</td><td className="text-right font-medium">{fmtMoney(doc.subtotal_no_objeto)}</td></tr>
                    <tr className="border-b"><td className="text-gray-600">Exento</td><td className="text-right font-medium">{fmtMoney(doc.subtotal_exento)}</td></tr>
                    <tr className="border-b bg-purple-50"><td className="text-purple-700 font-semibold">IVA</td><td className="text-right font-bold text-purple-700">{fmtMoney(doc.iva)}</td></tr>
                    {doc.ice > 0 && <tr className="border-b"><td className="text-gray-600">ICE</td><td className="text-right font-medium">{fmtMoney(doc.ice)}</td></tr>}
                    {doc.irbpnr > 0 && <tr className="border-b"><td className="text-gray-600">IRBPNR</td><td className="text-right font-medium">{fmtMoney(doc.irbpnr)}</td></tr>}
                  </tbody>
                </table>
              </div>
              <div className="bg-white border rounded-lg p-4">
                <h4 className="text-sm font-bold text-gray-800 mb-3">Totales</h4>
                <table className="w-full text-xs">
                  <tbody className="[&>tr>td]:py-1">
                    <tr className="border-b"><td className="text-gray-600">Subtotal sin Imp.</td><td className="text-right font-medium">{fmtMoney(doc.subtotal_sin_impuestos)}</td></tr>
                    <tr className="border-b"><td className="text-gray-600">Descuento</td><td className="text-right font-medium text-red-600">{fmtMoney(doc.descuento)}</td></tr>
                    {doc.propina > 0 && <tr className="border-b"><td className="text-gray-600">Propina</td><td className="text-right font-medium">{fmtMoney(doc.propina)}</td></tr>}
                    <tr className="border-b"><td className="text-gray-600">IVA</td><td className="text-right font-medium text-purple-700">{fmtMoney(doc.iva)}</td></tr>
                    <tr className="bg-emerald-50"><td className="text-emerald-700 font-bold">TOTAL</td><td className="text-right font-bold text-emerald-700 text-base">{fmtMoney(doc.total)}</td></tr>
                    <tr><td className="text-gray-500 text-[10px]" colSpan="2">Moneda: {doc.moneda || 'DOLAR'}</td></tr>
                  </tbody>
                </table>
                <div className="mt-3 pt-3 border-t text-xs space-y-1">
                  <div className="flex justify-between"><span className="text-gray-500">Categoría:</span><span>{doc.categoria || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Gasto personal:</span><span>{GP_LABEL[doc.gasto_personal_tipo] || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Deducible IR:</span><span>{doc.deducible_ir ? '✓ Sí' : '—'}</span></div>
                </div>
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

const KANBAN_COLUMNS = [
  {
    id: 'resumen',
    title: 'Resumen',
    subtitle: 'Panel general',
    icon: Layers,
    color: 'from-indigo-500 to-blue-600',
    accent: 'bg-indigo-500',
    tint: 'bg-indigo-50 border-indigo-200',
  },
  {
    id: 'ingresos',
    title: 'Ingresos',
    subtitle: 'Ventas emitidas',
    icon: TrendingUp,
    color: 'from-emerald-500 to-teal-600',
    accent: 'bg-emerald-500',
    tint: 'bg-emerald-50 border-emerald-200',
  },
  {
    id: 'egresos',
    title: 'Egresos',
    subtitle: 'Compras recibidas',
    icon: TrendingDown,
    color: 'from-red-500 to-rose-600',
    accent: 'bg-red-500',
    tint: 'bg-red-50 border-red-200',
  },
  {
    id: 'retenciones',
    title: 'Retenciones',
    subtitle: 'F-103 IVA/RIR',
    icon: Receipt,
    color: 'from-purple-500 to-fuchsia-600',
    accent: 'bg-purple-500',
    tint: 'bg-purple-50 border-purple-200',
  },
  {
    id: 'gastos_personales',
    title: 'Gastos Personales',
    subtitle: 'Anexo APS',
    icon: ClipboardList,
    color: 'from-amber-500 to-orange-600',
    accent: 'bg-amber-500',
    tint: 'bg-amber-50 border-amber-200',
  },
  {
    id: 'declaraciones',
    title: 'Declaraciones',
    subtitle: 'F-104 / F-102 / F-101',
    icon: Calculator,
    color: 'from-cyan-500 to-sky-600',
    accent: 'bg-cyan-500',
    tint: 'bg-cyan-50 border-cyan-200',
  },
];

const KanbanMenu = ({ active, onSelect, summary, docs }) => {
  const kpis = useMemo(() => {
    const ing = summary?.ingresos || {};
    const egr = summary?.egresos || {};
    const ret = summary?.retenciones || {};
    const iva = summary?.iva_f104 || {};
    const gp = summary?.gastos_personales || {};
    return {
      resumen: {
        primary: fmtMoney((ing.total || 0) - (egr.total || 0)),
        primaryLabel: 'Utilidad bruta',
        rows: [
          { l: 'Ingresos', v: fmtMoney(ing.total) },
          { l: 'Egresos', v: fmtMoney(egr.total) },
          { l: 'IVA saldo', v: fmtMoney(iva.saldo) },
        ],
      },
      ingresos: {
        primary: fmtMoney(ing.total),
        primaryLabel: `${ing.count || 0} comprobantes`,
        rows: [
          { l: 'Facturas', v: ing.facturas || 0 },
          { l: 'IVA cobrado', v: fmtMoney(ing.iva) },
          { l: 'N. Crédito', v: ing.notas_credito || 0 },
        ],
      },
      egresos: {
        primary: fmtMoney(egr.total),
        primaryLabel: `${egr.count || 0} comprobantes`,
        rows: [
          { l: 'Facturas', v: egr.facturas || 0 },
          { l: 'IVA pagado', v: fmtMoney(egr.iva) },
          { l: 'Liquidaciones', v: egr.liquidaciones || 0 },
        ],
      },
      retenciones: {
        primary: fmtMoney((ret.recibidas?.total || 0) + (ret.emitidas?.total || 0)),
        primaryLabel: 'Total periodo',
        rows: [
          { l: 'Me retuvieron', v: fmtMoney(ret.recibidas?.total) },
          { l: 'Yo retuve', v: fmtMoney(ret.emitidas?.total) },
          { l: 'Documentos', v: (ret.recibidas?.count || 0) + (ret.emitidas?.count || 0) },
        ],
      },
      gastos_personales: {
        primary: fmtMoney(gp.total),
        primaryLabel: 'Deducibles APS',
        rows: [
          { l: 'Vivienda', v: fmtMoney(gp.por_categoria?.vivienda?.total) },
          { l: 'Salud', v: fmtMoney(gp.por_categoria?.salud?.total) },
          { l: 'Educación', v: fmtMoney(gp.por_categoria?.educacion?.total) },
        ],
      },
      declaraciones: {
        primary: fmtMoney(Math.abs(iva.saldo || 0)),
        primaryLabel: (iva.saldo || 0) >= 0 ? 'IVA a pagar' : 'Crédito favor',
        rows: [
          { l: 'IVA cobrado', v: fmtMoney(iva.iva_cobrado) },
          { l: 'IVA pagado', v: fmtMoney(iva.iva_pagado) },
          { l: 'Gastos personales', v: fmtMoney(gp.total) },
        ],
      },
    };
  }, [summary]);

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white rounded-xl border p-3" data-testid="kanban-menu">
      <div className="flex gap-3 overflow-x-auto pb-2 scroll-smooth snap-x">
        {KANBAN_COLUMNS.map(col => {
          const isActive = active === col.id;
          const k = kpis[col.id] || {};
          const Icon = col.icon;
          return (
            <button
              key={col.id}
              onClick={() => onSelect(col.id)}
              className={`snap-start flex-shrink-0 w-[240px] rounded-xl overflow-hidden border-2 transition-all text-left group
                ${isActive ? 'border-[#0F1225] shadow-lg scale-[1.02]' : 'border-transparent hover:border-gray-300 hover:shadow'}`}
              data-testid={`kanban-col-${col.id}`}
            >
              {/* Column header (bg gradient) */}
              <div className={`bg-gradient-to-br ${col.color} px-4 py-3 text-white`}>
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-white/20"><Icon className="w-4 h-4" /></div>
                  <div>
                    <div className="text-sm font-bold leading-tight">{col.title}</div>
                    <div className="text-[10px] text-white/80">{col.subtitle}</div>
                  </div>
                  <ChevronRight className={`w-4 h-4 ml-auto transition ${isActive ? 'rotate-90' : 'group-hover:translate-x-0.5'}`} />
                </div>
              </div>
              {/* KPI body */}
              <div className={`p-3 space-y-2 ${col.tint} border-t`}>
                <div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-wide">{k.primaryLabel}</div>
                  <div className="text-lg font-bold text-[#0F1225]">{k.primary}</div>
                </div>
                <div className="pt-2 border-t border-white/60 space-y-1">
                  {(k.rows || []).map((r, i) => (
                    <div key={i} className="flex justify-between text-[11px]">
                      <span className="text-gray-600">{r.l}</span>
                      <span className="font-mono font-semibold text-gray-800">{r.v}</span>
                    </div>
                  ))}
                </div>
                {/* Progress strip */}
                <div className="h-1 w-full bg-white/60 rounded-full overflow-hidden mt-2">
                  <div className={`h-full ${col.accent} transition-all`} style={{ width: isActive ? '100%' : '20%' }}></div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ═════════ TABS ═════════

const ResumenTab = ({ summary, period }) => {
  if (!summary) return <div className="text-center text-gray-400 py-8">Cargando resumen…</div>;
  const monthLabel = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'][period.month - 1];
  const ing = summary.ingresos || {};
  const egr = summary.egresos || {};
  const ret = summary.retenciones || {};
  const iva = summary.iva_f104 || {};
  const util = summary.utilidad_bruta_estimada || 0;
  return (
    <div className="space-y-4" data-testid="resumen-tab">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KpiCard icon={<TrendingUp className="w-5 h-5 text-emerald-600" />} label="Ingresos" value={fmtMoney(ing.total)}
                  sub={`${ing.count || 0} comprobantes emitidos`} tint="bg-emerald-50 border-emerald-200" />
        <KpiCard icon={<TrendingDown className="w-5 h-5 text-red-600" />} label="Egresos" value={fmtMoney(egr.total)}
                  sub={`${egr.count || 0} comprobantes recibidos`} tint="bg-red-50 border-red-200" />
        <KpiCard icon={<Calculator className="w-5 h-5 text-blue-600" />} label="Utilidad Bruta" value={fmtMoney(util)}
                  sub={`${util >= 0 ? 'Positiva' : 'Negativa'}`} tint={util >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'} />
        <KpiCard icon={<Receipt className="w-5 h-5 text-purple-600" />} label="IVA a Pagar" value={fmtMoney(iva.a_pagar)}
                  sub={iva.credito_favor > 0 ? `Crédito a favor: ${fmtMoney(iva.credito_favor)}` : `${monthLabel} ${period.year}`}
                  tint="bg-purple-50 border-purple-200" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border p-4">
          <h4 className="font-semibold text-sm mb-3 flex items-center gap-2"><PieChartIcon className="w-4 h-4 text-emerald-600" /> Detalle de Ingresos ({monthLabel} {period.year})</h4>
          <SubtotalRows data={ing} />
        </div>
        <div className="bg-white rounded-xl border p-4">
          <h4 className="font-semibold text-sm mb-3 flex items-center gap-2"><PieChartIcon className="w-4 h-4 text-red-600" /> Detalle de Egresos ({monthLabel} {period.year})</h4>
          <SubtotalRows data={egr} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border p-4">
          <h4 className="font-semibold text-sm mb-3 flex items-center gap-2"><Receipt className="w-4 h-4 text-purple-600" /> Retenciones</h4>
          <div className="space-y-2 text-sm">
            <RowKV label="Me retuvieron (recibidas)" value={`${fmtMoney(ret.recibidas?.total)} · ${ret.recibidas?.count || 0} docs`} />
            <RowKV label="Yo retuve (emitidas)" value={`${fmtMoney(ret.emitidas?.total)} · ${ret.emitidas?.count || 0} docs`} />
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <h4 className="font-semibold text-sm mb-3 flex items-center gap-2"><ClipboardList className="w-4 h-4 text-cyan-600" /> Formulario 104 - IVA</h4>
          <div className="space-y-2 text-sm">
            <RowKV label="IVA cobrado (ventas)" value={fmtMoney(iva.iva_cobrado)} />
            <RowKV label="IVA pagado (compras)" value={fmtMoney(iva.iva_pagado)} />
            <div className="pt-2 border-t"></div>
            <RowKV label={iva.saldo >= 0 ? 'A pagar al SRI' : 'Crédito a favor'} value={fmtMoney(Math.abs(iva.saldo))}
                    highlight={iva.saldo >= 0 ? 'text-red-600' : 'text-emerald-600'} />
          </div>
        </div>
      </div>
    </div>
  );
};

const KpiCard = ({ icon, label, value, sub, tint }) => (
  <div className={`rounded-xl border ${tint} p-4`}>
    <div className="flex items-center gap-2 mb-2">{icon}<span className="text-xs text-gray-600 font-semibold">{label}</span></div>
    <div className="text-2xl font-bold text-[#0F1225]">{value}</div>
    <div className="text-xs text-gray-500 mt-1">{sub}</div>
  </div>
);

const SubtotalRows = ({ data = {} }) => (
  <div className="space-y-1.5 text-sm">
    <RowKV label="Subtotal 0%" value={fmtMoney(data.subtotal_0)} />
    <RowKV label="Subtotal 12%" value={fmtMoney(data.subtotal_12)} />
    <RowKV label="Subtotal 15%" value={fmtMoney(data.subtotal_15)} />
    <RowKV label="Exento" value={fmtMoney(data.subtotal_exento)} />
    <RowKV label="No objeto de IVA" value={fmtMoney(data.subtotal_no_objeto)} />
    <div className="pt-1 border-t"></div>
    <RowKV label="IVA total" value={fmtMoney(data.iva)} highlight="text-purple-700" />
    <RowKV label="Total con impuestos" value={fmtMoney(data.total)} highlight="font-bold" />
    <div className="text-[10px] text-gray-400 mt-1">Facturas: {data.facturas || 0} · N. Crédito: {data.notas_credito || 0} · N. Débito: {data.notas_debito || 0} · Liquidaciones: {data.liquidaciones || 0}</div>
  </div>
);

const RowKV = ({ label, value, highlight = '' }) => (
  <div className="flex justify-between">
    <span className="text-gray-600">{label}</span>
    <span className={`font-mono ${highlight}`}>{value}</span>
  </div>
);

const DocumentTable = ({ docs, type, filters, setFilters, onReload, categorias }) => {
  const [detailDoc, setDetailDoc] = useState(null);
  const direccion = type === 'ingresos' ? 'emitidos' : 'recibidos';
  useEffect(() => { setFilters(f => ({ ...f, direccion })); /* eslint-disable-next-line */ }, [type]);
  const shown = docs.filter(d => d.direccion === direccion);
  const updateDoc = async (id, patch) => {
    try {
      await axios.put(`${API}/contabilidad/documents/${id}`, patch, { headers: authHeaders() });
      onReload();
    } catch (e) { toast.error(parseError(e)); }
  };
  return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid={`${type}-table`}>
      <div className="p-3 border-b flex items-center gap-2 flex-wrap">
        <span className="text-sm font-semibold">{type === 'ingresos' ? 'Registro de Ingresos' : 'Registro de Egresos'}</span>
        <span className="text-xs text-gray-500">({shown.length} documentos)</span>
        <div className="ml-auto flex gap-2">
          <select value={filters.tipo} onChange={e => setFilters(f => ({ ...f, tipo: e.target.value }))}
                  className="text-xs border rounded px-2 py-1">
            <option value="">Todos los tipos</option>
            <option value="factura">Facturas</option>
            <option value="nota_credito">Notas Crédito</option>
            <option value="nota_debito">Notas Débito</option>
            <option value="liquidacion">Liquidaciones</option>
          </select>
          <select value={filters.categoria} onChange={e => setFilters(f => ({ ...f, categoria: e.target.value }))}
                  className="text-xs border rounded px-2 py-1">
            <option value="">Todas las categorías</option>
            {categorias.categorias_gasto?.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left">Fecha</th>
              <th className="px-3 py-2 text-left">Tipo</th>
              <th className="px-3 py-2 text-left">Emisor / RUC</th>
              <th className="px-3 py-2 text-left">Documento</th>
              <th className="px-3 py-2 text-right">Sub 12%</th>
              <th className="px-3 py-2 text-right">Sub 15%</th>
              <th className="px-3 py-2 text-right">IVA</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-left">Categoría</th>
              <th className="px-3 py-2 text-center w-10">Ver</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 ? (
              <tr><td colSpan="10" className="text-center text-gray-400 py-8">Sin documentos. Sincroniza con SRI o sube XMLs.</td></tr>
            ) : shown.map(d => (
              <tr key={d.id} className="border-t hover:bg-gray-50" data-testid={`doc-row-${d.id}`}>
                <td className="px-3 py-2">{fmtDate(d.fecha_emision)}</td>
                <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded text-[10px] border ${TIPO_BADGE[d.tipo_comprobante] || TIPO_BADGE.otro}`}>{TIPO_LABEL[d.tipo_comprobante] || d.tipo_comprobante}</span></td>
                <td className="px-3 py-2">
                  <div className="font-medium">{d.razon_social_emisor}</div>
                  <div className="text-[10px] text-gray-500 font-mono">{d.ruc_emisor}</div>
                </td>
                <td className="px-3 py-2 font-mono text-[10px]">{d.numero_documento}</td>
                <td className="px-3 py-2 text-right">{fmtMoney(d.subtotal_12)}</td>
                <td className="px-3 py-2 text-right">{fmtMoney(d.subtotal_15)}</td>
                <td className="px-3 py-2 text-right text-purple-700">{fmtMoney(d.iva)}</td>
                <td className="px-3 py-2 text-right font-semibold">{fmtMoney(d.total)}</td>
                <td className="px-3 py-2">
                  <select value={d.categoria || ''} onChange={e => updateDoc(d.id, { categoria: e.target.value || null })}
                          className="text-[10px] border rounded px-1 py-0.5 max-w-[130px]">
                    <option value="">— sin categoría —</option>
                    {categorias.categorias_gasto?.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2 text-center">
                  <button onClick={() => setDetailDoc(d)}
                          className="p-1 rounded hover:bg-emerald-100 text-emerald-700"
                          title="Ver detalle e items"
                          data-testid={`doc-view-${d.id}`}>
                    <Eye className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {detailDoc && <DocumentDetailModal docId={detailDoc.id} onClose={() => setDetailDoc(null)} />}
    </div>
  );
};

const RetencionesTab = ({ docs }) => {
  const [detailDoc, setDetailDoc] = useState(null);
  const total = docs.reduce((s, d) => s + (d.total || 0), 0);
  return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid="retenciones-tab">
      <div className="p-3 border-b flex items-center gap-2">
        <span className="text-sm font-semibold">Listado de Retenciones</span>
        <span className="text-xs text-gray-500">({docs.length} · Total: {fmtMoney(total)})</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left">Fecha</th>
              <th className="px-3 py-2 text-left">Dirección</th>
              <th className="px-3 py-2 text-left">Emisor</th>
              <th className="px-3 py-2 text-left">Documento</th>
              <th className="px-3 py-2 text-left">Detalle</th>
              <th className="px-3 py-2 text-right">Base</th>
              <th className="px-3 py-2 text-right">%</th>
              <th className="px-3 py-2 text-right">Retenido</th>
              <th className="px-3 py-2 text-center w-10">Ver</th>
            </tr>
          </thead>
          <tbody>
            {docs.length === 0 ? (
              <tr><td colSpan="9" className="text-center text-gray-400 py-8">Sin retenciones en el período.</td></tr>
            ) : docs.flatMap(d => (d.retenciones && d.retenciones.length > 0 ? d.retenciones : [{}]).map((r, i) => (
              <tr key={`${d.id}-${i}`} className="border-t hover:bg-gray-50">
                {i === 0 && <>
                  <td className="px-3 py-2" rowSpan={d.retenciones?.length || 1}>{fmtDate(d.fecha_emision)}</td>
                  <td className="px-3 py-2" rowSpan={d.retenciones?.length || 1}>
                    <span className={`px-2 py-0.5 rounded text-[10px] border ${d.direccion === 'recibidos' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                      {d.direccion === 'recibidos' ? 'Me retuvieron' : 'Yo retuve'}
                    </span>
                  </td>
                  <td className="px-3 py-2" rowSpan={d.retenciones?.length || 1}>
                    <div>{d.razon_social_emisor}</div>
                    <div className="text-[10px] text-gray-500 font-mono">{d.ruc_emisor}</div>
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px]" rowSpan={d.retenciones?.length || 1}>{d.numero_documento}</td>
                </>}
                <td className="px-3 py-2 font-mono text-[10px]">{r.codigo_retencion || r.codigo || '—'}</td>
                <td className="px-3 py-2 text-right">{fmtMoney(r.base_imponible)}</td>
                <td className="px-3 py-2 text-right">{r.porcentaje ? `${r.porcentaje}%` : '—'}</td>
                <td className="px-3 py-2 text-right font-semibold text-purple-700">{fmtMoney(r.valor_retenido)}</td>
                {i === 0 && (
                  <td className="px-3 py-2 text-center" rowSpan={d.retenciones?.length || 1}>
                    <button onClick={() => setDetailDoc(d)} className="p-1 rounded hover:bg-emerald-100 text-emerald-700" title="Ver detalle">
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                )}
              </tr>
            )))}
          </tbody>
        </table>
      </div>
      {detailDoc && <DocumentDetailModal docId={detailDoc.id} onClose={() => setDetailDoc(null)} />}
    </div>
  );
};

const GastosPersonalesTab = ({ summary, docs, onReload, categorias }) => {
  const [detailDoc, setDetailDoc] = useState(null);
  const gp = summary?.gastos_personales?.por_categoria || {};
  const total = summary?.gastos_personales?.total || 0;
  const updateDoc = async (id, patch) => {
    try {
      await axios.put(`${API}/contabilidad/documents/${id}`, patch, { headers: authHeaders() });
      onReload();
    } catch (e) { toast.error(parseError(e)); }
  };
  return (
    <div className="space-y-4" data-testid="gastos-personales-tab">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {Object.entries(GP_LABEL).map(([key, label]) => (
          <div key={key} className="bg-white rounded-xl border p-3 text-center">
            <div className="text-xs text-gray-500">{label}</div>
            <div className="text-lg font-bold text-[#2F3D8E]">{fmtMoney(gp[key]?.total)}</div>
            <div className="text-[10px] text-gray-400">{gp[key]?.count || 0} docs</div>
          </div>
        ))}
      </div>
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3">
        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-emerald-900">Total gastos personales deducibles (Anexo APS)</div>
          <div className="text-xs text-emerald-700">Este monto se aplica según los topes SRI 2026 en el Anexo de Gastos Personales.</div>
        </div>
        <div className="text-2xl font-bold text-emerald-700">{fmtMoney(total)}</div>
      </div>

      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="p-3 border-b text-sm font-semibold">Clasificación de documentos (Egresos)</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left">Fecha</th>
                <th className="px-3 py-2 text-left">Emisor</th>
                <th className="px-3 py-2 text-left">Documento</th>
                <th className="px-3 py-2 text-right">Total</th>
                <th className="px-3 py-2 text-left">Gasto personal</th>
                <th className="px-3 py-2 text-left">Deducible</th>
                <th className="px-3 py-2 text-center w-10">Ver</th>
              </tr>
            </thead>
            <tbody>
              {docs.filter(d => d.tipo_comprobante === 'factura' || d.tipo_comprobante === 'liquidacion').map(d => (
                <tr key={d.id} className="border-t hover:bg-gray-50">
                  <td className="px-3 py-2">{fmtDate(d.fecha_emision)}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{d.razon_social_emisor}</div>
                    <div className="text-[10px] text-gray-500 font-mono">{d.ruc_emisor}</div>
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px]">{d.numero_documento}</td>
                  <td className="px-3 py-2 text-right font-semibold">{fmtMoney(d.total)}</td>
                  <td className="px-3 py-2">
                    <select value={d.gasto_personal_tipo || ''} onChange={e => updateDoc(d.id, { gasto_personal_tipo: e.target.value || null, deducible_ir: !!e.target.value })}
                            className="text-[10px] border rounded px-1 py-0.5">
                      <option value="">— no aplica —</option>
                      {Object.entries(GP_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <input type="checkbox" checked={!!d.deducible_ir} onChange={e => updateDoc(d.id, { deducible_ir: e.target.checked })} />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button onClick={() => setDetailDoc(d)} className="p-1 rounded hover:bg-emerald-100 text-emerald-700" title="Ver detalle">
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {docs.filter(d => d.tipo_comprobante === 'factura' || d.tipo_comprobante === 'liquidacion').length === 0 && (
                <tr><td colSpan="7" className="text-center text-gray-400 py-8">Sin documentos.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {detailDoc && <DocumentDetailModal docId={detailDoc.id} onClose={() => setDetailDoc(null)} />}
    </div>
  );
};

const DeclaracionesTab = ({ summary, period, ws }) => {
  const iva = summary?.iva_f104 || {};
  const monthLabel = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'][period.month - 1];
  return (
    <div className="space-y-4" data-testid="declaraciones-tab">
      <div className="bg-white rounded-xl border p-4">
        <h3 className="text-lg font-bold text-[#0F1225] mb-1">📋 Formulario 104 - IVA mensual</h3>
        <div className="text-xs text-gray-500 mb-3">{ws?.razon_social} · RUC {ws?.ruc} · Período: {monthLabel} {period.year}</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border rounded-lg p-3">
            <div className="text-xs font-semibold text-gray-700 mb-2">VENTAS (Ingresos)</div>
            <SubtotalRows data={summary?.ingresos || {}} />
          </div>
          <div className="border rounded-lg p-3">
            <div className="text-xs font-semibold text-gray-700 mb-2">COMPRAS (Egresos)</div>
            <SubtotalRows data={summary?.egresos || {}} />
          </div>
        </div>
        <div className="mt-4 p-3 rounded-lg bg-purple-50 border border-purple-200 grid grid-cols-3 gap-3 text-sm">
          <div>
            <div className="text-xs text-purple-700">IVA cobrado en ventas</div>
            <div className="font-bold text-purple-900">{fmtMoney(iva.iva_cobrado)}</div>
          </div>
          <div>
            <div className="text-xs text-purple-700">Crédito tributario (IVA compras)</div>
            <div className="font-bold text-purple-900">{fmtMoney(iva.iva_pagado)}</div>
          </div>
          <div>
            <div className="text-xs text-purple-700">{iva.saldo >= 0 ? 'IVA a pagar' : 'Crédito a favor'}</div>
            <div className={`font-bold ${iva.saldo >= 0 ? 'text-red-700' : 'text-emerald-700'}`}>{fmtMoney(Math.abs(iva.saldo))}</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border p-4">
        <h3 className="text-lg font-bold text-[#0F1225] mb-1">📋 Anexo de Gastos Personales (APS)</h3>
        <div className="text-xs text-gray-500 mb-3">Deducibles según normativa SRI Ecuador 2026</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {Object.entries(GP_LABEL).map(([k, l]) => (
            <div key={k} className="border rounded-lg p-3 flex justify-between items-center">
              <span className="text-sm">{l}</span>
              <span className="font-mono font-bold">{fmtMoney(summary?.gastos_personales?.por_categoria?.[k]?.total)}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200 flex justify-between items-center">
          <span className="text-sm font-semibold text-emerald-900">Total anexo APS</span>
          <span className="text-2xl font-bold text-emerald-700">{fmtMoney(summary?.gastos_personales?.total)}</span>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
        <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          <div className="font-semibold mb-1">Próximas fases (roadmap)</div>
          <ul className="list-disc list-inside text-xs space-y-1">
            <li>Formulario 103 (Retenciones mensuales) auto-generado</li>
            <li>Formulario 102 (Renta Personas Naturales) / 101 (Sociedades)</li>
            <li>ATS (Anexo Transaccional Simplificado) exportable</li>
            <li>Balance General + Estado de Resultados con plan de cuentas configurable</li>
            <li>Exportar Excel/PDF y presentar directamente al SRI</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ContabilidadPage;
